import type { MarketplaceKey } from "@porsche-customer/slfinrtl-context-types"
import type { AddMarketExportBody, MarketExportJobStatusView, MarketExportJobView } from "@slfinrtl/ui-bff-routes"
import { addMarketExportRoute, getMarketExportJobRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext } from "react"
import { downloadFromUrl } from "src/common/downloadBlob/downloadFromUrl"
import { getAxiosInstance } from "../../common/getAxiosInstance/getAxiosInstance"
import { RequestContext } from "../../requestContext/RequestContext"

const POLL_INTERVAL_MS = 1_000
const POLL_TIMEOUT_MS = 300_000

const wait = (durationMs: number, signal?: AbortSignal) =>
	new Promise<void>((resolve, reject) => {
		signal?.throwIfAborted()
		const onAbort = () => {
			clearTimeout(timeout)
			reject(new DOMException("Market export aborted", "AbortError"))
		}
		const timeout = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort)
			resolve()
		}, durationMs)
		signal?.addEventListener("abort", onAbort, { once: true })
	})

/**
 * Creates a market export job in the BFF, polls it until the excel has been generated and then
 * downloads it from the returned pre-signed url.
 */
export const useMarketExport = (marketplaceKey: MarketplaceKey | null | undefined) => {
	const context = useContext(RequestContext)

	return useCallback(
		async (
			request: AddMarketExportBody,
			onProgress?: (progress: number | undefined) => void,
			signal?: AbortSignal,
		) => {
			if (!marketplaceKey) {
				return
			}

			signal?.throwIfAborted()
			const axiosInstance = await getAxiosInstance(context)
			signal?.throwIfAborted()
			const { data } = await axiosInstance[addMarketExportRoute.method]<MarketExportJobView>(
				addMarketExportRoute.buildUrl({ marketplaceKey }),
				request,
				{ signal },
			)
			signal?.throwIfAborted()

			const jobUrl = getMarketExportJobRoute.buildUrl({ exportJobId: data.exportJobId })
			const deadline = Date.now() + POLL_TIMEOUT_MS

			while (Date.now() < deadline) {
				await wait(POLL_INTERVAL_MS, signal)
				signal?.throwIfAborted()

				const { data: job } = await axiosInstance[getMarketExportJobRoute.method]<MarketExportJobStatusView>(
					jobUrl,
					{ signal },
				)
				signal?.throwIfAborted()
				onProgress?.(job.progress)
				signal?.throwIfAborted()

				if (job.status === "COMPLETED" && job.downloadUrl) {
					downloadFromUrl(
						job.downloadUrl,
						`Market_${marketplaceKey}${request.exportType === "DELIVERED_INVENTORY" ? "_delivered" : ""}.xlsx`,
					)
					return
				}

				if (job.status === "FAILED") {
					throw new Error(job.errorMessage ?? "Market export job failed")
				}
			}

			throw new Error("Market export job timed out")
		},
		[context, marketplaceKey],
	)
}
